import React, { createContext, useContext, useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { currentUserInfo, currentUserAccessToken, signInResponseErrorState, latestLoginTimeState, globalLoadingState } from "../atoms/signInAtom";
import axios from "../utils/axios";

export const DjangoAuthContext = createContext();

export const DjangoAuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [userInfo, setUserInfo] = useRecoilState(currentUserInfo);
  const [userAccessToken, setUserAccessToken] = useRecoilState(currentUserAccessToken);
  const [signInResponseError, setSignInResponseError] = useRecoilState(signInResponseErrorState);
  const [latestLoginTime, setLatestLoginTime] = useRecoilState(latestLoginTimeState);
  const [globalLoading, setGlobalLoading] = useRecoilState(globalLoadingState);

  // Check if user is logged in on app start
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      setUserAccessToken(token);
      axios.defaults.headers.common.Authorization = `Token ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      setGlobalLoading(true);
      const response = await axios.get("/api/account/me/");
      if (response.status === 200) {
        setUserInfo(response.data);
        setUser(response.data.user);
        setLatestLoginTime(new Date().getTime().toString());
        setGlobalLoading(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      logout();
      setGlobalLoading(false);
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setGlobalLoading(true);
      console.log("Attempting login with:", { email, password: password ? "***" : "empty" });
      
      const response = await axios.post("/api/account/login/", {
        email,
        password
      });
      
      console.log("Login response:", response);
      console.log("Response status:", response.status);
      console.log("Response data:", response.data);
      
      if (response.status === 200) {
        const { token, user } = response.data;
        console.log("Token:", token);
        console.log("User:", user);
        
        localStorage.setItem("access_token", token);
        setUserAccessToken(token);
        axios.defaults.headers.common.Authorization = `Token ${token}`;
        setUserInfo({ user });
        setUser(user);
        setLatestLoginTime(new Date().getTime().toString());
        setGlobalLoading(false);
        console.log("Login successful, returning success");
        return { success: true, user };
      } else {
        console.log("Unexpected status code:", response.status);
        setGlobalLoading(false);
        return { success: false, error: "Unexpected response status" };
      }
    } catch (error) {
      setGlobalLoading(false);
      console.error("Login error:", error);
      console.error("Error response:", error.response?.data);
      
      let errorMessage = "Login failed";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
    
    // If we get here, something went wrong
    setGlobalLoading(false);
    return { 
      success: false, 
      error: "Login failed - no response received"
    };
  };

  const logout = async () => {
    try {
      await axios.post("/api/account/logout/");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("access_token");
      setUserAccessToken(null);
      setUserInfo(null);
      setUser(null);
      setSignInResponseError(null);
      setLoading(false);
      setGlobalLoading(false);
      axios.defaults.headers.common.Authorization = "";
    }
  };

  const createUser = async (email, password) => {
    // This would need to be implemented based on your registration flow
    throw new Error("User registration not implemented yet");
  };

  const forgotPassword = async (email) => {
    // This would need to be implemented based on your password reset flow
    throw new Error("Password reset not implemented yet");
  };

  const value = {
    user,
    login,
    logout,
    createUser,
    forgotPassword,
    loading
  };

  return (
    <DjangoAuthContext.Provider value={value}>
      {children}
    </DjangoAuthContext.Provider>
  );
};

export const useDjangoAuth = () => {
  const context = useContext(DjangoAuthContext);
  if (!context) {
    throw new Error("useDjangoAuth must be used within a DjangoAuthContextProvider");
  }
  return context;
};
